import { useAuth } from "@/lib/auth-context";
import { useVaultModeStore, type VaultMode } from "@/stores/vault-mode-store";

export type { VaultMode };

export interface VaultModeInfo {
  loading: boolean;
  /** The user owns at least one vault. */
  isOwner: boolean;
  /** The user is a trustee/executor of at least one vault. */
  isHeir: boolean;
  /** True when the user holds both roles and must choose which to act as. */
  hasBoth: boolean;
  /**
   * The mode the app should render, reconciling the persisted choice with the
   * roles actually on the token:
   *  - a single-role user is forced into that role (stored choice ignored);
   *  - a dual-role user gets their stored choice, or `null` until they pick;
   *  - a user with no vault relationships is `null` with `hasBoth === false`.
   */
  effectiveMode: VaultMode | null;
  setMode: (mode: VaultMode | null) => void;
}

/**
 * Single source of truth for owner-vs-heir gating across the vault app. Derives
 * capabilities from the bounded `vault_is_owner` / `vault_is_heir` claims and
 * resolves the active mode. Per-vault authorization is enforced server-side.
 */
export function useVaultMode(): VaultModeInfo {
  const { vaultCapabilities, loading } = useAuth();
  const activeMode = useVaultModeStore((s) => s.activeMode);
  const setMode = useVaultModeStore((s) => s.setActiveMode);

  const { isOwner, isHeir } = vaultCapabilities;
  const hasBoth = isOwner && isHeir;

  let effectiveMode: VaultMode | null = null;
  if (isOwner && !isHeir) effectiveMode = "owner";
  else if (isHeir && !isOwner) effectiveMode = "heir";
  else if (hasBoth) effectiveMode = activeMode; // may be null → show the picker

  return { loading, isOwner, isHeir, hasBoth, effectiveMode, setMode };
}
